import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNewCustomer } from "@/context/NewCustomerContext";
import api from "@/lib/api";
import { BrandLogo } from "@/components/brand/BrandLogo";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationBell from "@/components/NotificationBell";
import ChatButton from "@/components/chat/ChatButton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Users, Flame, Sun, Target, FileText, CalendarDays, CheckSquare, MessageCircle,
  Briefcase, BarChart3, Building2, LogOut, Menu, X, Plus, ChevronLeft,
  MapPin, UserCircle, ShieldCheck, PanelLeft, Factory, Gauge, XOctagon, CalendarClock, DatabaseZap, Inbox, CalendarCheck, MessageSquareText, Activity,
} from "lucide-react";

const MAIN_NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, testid: "nav-home" },
  { to: "/chat", label: "Chat interna", icon: MessageSquareText, testid: "nav-chat" },
  { to: "/coda-lavori", label: "Coda Lavori / Lead", icon: Inbox, testid: "nav-coda-lavori" },
  { to: "/monitoraggio", label: "Monitoraggio Sedi", icon: Activity, testid: "nav-monitoraggio" },
  { to: "/clienti", label: "Clienti", icon: Users, testid: "nav-clienti" },
  { to: "/forniture", label: "Forniture & Scadenze", icon: Flame, testid: "nav-forniture" },
  { to: "/efficienza", label: "Efficienza Energetica", icon: Sun, testid: "nav-efficienza" },
  { to: "/opportunita", label: "Opportunità", icon: Target, testid: "nav-opportunita" },
  { to: "/preventivi", label: "Preventivi", icon: FileText, testid: "nav-preventivi" },
  { to: "/appuntamenti", label: "Appuntamenti", icon: CalendarDays, testid: "nav-appuntamenti" },
  { to: "/attivita", label: "Attività", icon: CheckSquare, testid: "nav-attivita" },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, testid: "nav-whatsapp" },
  { to: "/produzione", label: "Produzione", icon: Factory, testid: "nav-produzione" },
  { to: "/target", label: "Target", icon: Gauge, testid: "nav-target" },
  { to: "/ko", label: "KO / Annullamenti", icon: XOctagon, testid: "nav-ko" },
  { to: "/turni", label: "Turni", icon: CalendarClock, testid: "nav-turni" },
  { to: "/portafoglio", label: "Portafoglio Commerciale", icon: Briefcase, testid: "nav-portafoglio" },
];

const ADMIN_NAV = [
  { to: "/dashboard-direzione", label: "Dashboard Direzione", icon: BarChart3, testid: "nav-dashboard-direzione" },
  { to: "/utenti", label: "Gestione Utenti", icon: Users, testid: "nav-users" },
  { to: "/sedi", label: "Gestione Sedi", icon: Building2, testid: "nav-offices" },
  { to: "/impostazioni/google", label: "Google Calendar", icon: CalendarCheck, testid: "nav-google" },
  { to: "/migrazione", label: "Migrazione dati", icon: DatabaseZap, testid: "nav-migrazione" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { openNew } = useNewCustomer();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("crm_sidebar_collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [officeName, setOfficeName] = useState("");

  const isAdmin = user?.role === "admin";
  const isOperator = user?.role === "operatore";
  const mainNav = isOperator && !user?.efficienza_global ? MAIN_NAV.filter((i) => i.to !== "/efficienza" && i.to !== "/opportunita") : MAIN_NAV;

  useEffect(() => {
    localStorage.setItem("crm_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    if (!user?.office_id) return;
    (async () => {
      try {
        const { data } = await api.get("/offices");
        setOfficeName(data.find((o) => o.id === user.office_id)?.name || "");
      } catch (e) { /* ignore */ }
    })();
  }, [user?.office_id]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavGroup = ({ items, heading }) => (
    <div className="space-y-1">
      {heading && !collapsed && (
        <div className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-widest text-sidebar-muted">{heading}</div>
      )}
      {heading && collapsed && <div className="mx-3 my-3 border-t border-white/10" />}
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          data-testid={item.testid}
          title={collapsed ? item.label : undefined}
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              collapsed ? "justify-center" : ""
            } ${isActive
              ? "bg-sidebar-accent text-white shadow-sm shadow-black/20"
              : "text-sidebar-foreground/80 hover:bg-white/10 hover:text-white"}`
          }
        >
          <item.icon className="h-[18px] w-[18px] shrink-0" />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </NavLink>
      ))}
    </div>
  );

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className={`flex h-16 items-center justify-center border-b border-white/10 ${collapsed ? "px-2" : "px-5"}`}>
        {collapsed
          ? <BrandLogo onDark mono />
          : <BrandLogo onDark widthClass="w-[155px]" subtitle="CRM Interno" />}
      </div>
      <nav className="ds-scroll flex-1 overflow-y-auto px-3 py-3">
        <NavGroup items={mainNav} />
        {isAdmin && <NavGroup items={ADMIN_NAV} heading="Amministrazione" />}
      </nav>
      <div className="border-t border-white/10 p-3">
        <button
          onClick={() => setCollapsed((v) => !v)}
          data-testid="sidebar-collapse-toggle"
          className={`hidden w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground/70 transition-colors hover:bg-white/10 hover:text-white lg:flex ${collapsed ? "justify-center" : ""}`}
          title={collapsed ? "Espandi menu" : "Riduci menu"}
        >
          <ChevronLeft className={`h-[18px] w-[18px] transition-transform ${collapsed ? "rotate-180" : ""}`} />
          {!collapsed && <span>Riduci menu</span>}
        </button>
      </div>
    </div>
  );

  const sidebarW = collapsed ? "lg:w-20" : "lg:w-64";
  const contentPad = collapsed ? "lg:pl-20" : "lg:pl-64";

  return (
    <div className="min-h-screen bg-background">
      <aside className={`fixed inset-y-0 left-0 z-30 hidden bg-sidebar transition-all duration-200 lg:block ${sidebarW}`}>
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-sidebar">
            <button className="absolute right-3 top-4 text-sidebar-muted" onClick={() => setMobileOpen(false)} data-testid="close-mobile-menu">
              <X className="h-5 w-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className={`transition-all duration-200 ${contentPad}`}>
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-white/85 px-4 backdrop-blur lg:px-6">
          <button className="text-slate-600 lg:hidden" onClick={() => setMobileOpen(true)} data-testid="open-mobile-menu">
            <Menu className="h-6 w-6" />
          </button>
          {/* Richiamo brand discreto nella top bar (mobile: sidebar nascosta) */}
          <span className="inline-flex items-center lg:hidden" data-testid="header-brand">
            <img src="/brand-logo.png" alt="ESSECI E-LIGHT" className="h-6 w-auto object-contain" />
          </span>
          <button className="hidden text-slate-500 transition-colors hover:text-brand lg:block" onClick={() => setCollapsed((v) => !v)} data-testid="header-collapse-toggle" title="Riduci/Espandi menu">
            <PanelLeft className="h-5 w-5" />
          </button>

          {/* Ricerca globale cliente */}
          <GlobalSearch />

          <Button
            data-testid="nuovo-cliente-button"
            className="ml-auto gap-1.5"
            onClick={openNew}
          >
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Nuovo Cliente</span>
          </Button>

          <ChatButton />

          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-slate-100" data-testid="account-menu-trigger">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand/10 text-brand">
                  <UserCircle className="h-5 w-5" />
                </div>
                <div className="hidden text-left leading-tight md:block">
                  <div className="max-w-[160px] truncate text-sm font-semibold text-slate-800">{user?.full_name}</div>
                  <div className="flex items-center gap-1 text-[11px] text-slate-500">
                    <MapPin className="h-3 w-3" /> {officeName || "—"}
                  </div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white">
              <DropdownMenuLabel>
                <div className="font-semibold text-slate-800">{user?.full_name}</div>
                <div className="mt-0.5 flex items-center gap-1 text-xs font-normal text-slate-500">
                  {user?.role === "admin" ? <><ShieldCheck className="h-3 w-3 text-brand" /> Direzione</>
                    : user?.role === "backoffice" ? <><ShieldCheck className="h-3 w-3 text-amber-600" /> Back Office</>
                    : <><UserCircle className="h-3 w-3" /> Operatore</>}
                  {officeName && <span>· {officeName}</span>}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="text-danger focus:text-danger">
                <LogOut className="mr-2 h-4 w-4" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
